import React, { useState, useEffect, useRef } from 'react';
import { collection, getDocs, doc, getDoc, query, orderBy, addDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL, listAll } from 'firebase/storage';
import { db, Storage } from '../firebase';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../style/Transaction.css';
import { jsPDF } from 'jspdf';
import autoTable from "jspdf-autotable";
import { ToWords } from "to-words";

const Transactions = () => {
    const [brands, setBrands] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedBrand, setSelectedBrand] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [isAddingTransaction, setIsAddingTransaction] = useState(false);
    const [isEditingTransaction, setIsEditingTransaction] = useState(false);
    const [isCreatingCreditNote, setIsCreatingCreditNote] = useState(false);
    const [transactionToEdit, setTransactionToEdit] = useState(null);
    const [selectedBrandDetails, setSelectedBrandDetails] = useState(null);
    const [expandedBulkOrders, setExpandedBulkOrders] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    // Using refs for form inputs
    const typeInputRef = useRef(null);
    const amountInputRef = useRef(null);
    const balanceBeforeInputRef = useRef(null);
    const balanceAfterInputRef = useRef(null);
    const timestampInputRef = useRef(null);
    
    // Credit note form refs
    const brandAddressRef = useRef(null);
    const brandCityRef = useRef(null);
    const brandStateRef = useRef(null);
    const brandPincodeRef = useRef(null);
    const brandGstRef = useRef(null);
    const itemDescriptionRef = useRef(null);
    const creditNoteAmountRef = useRef(null);


    const toggleBulkOrderDetails = (transactionId) => {
    setExpandedBulkOrders(prev => ({
        ...prev,
        [transactionId]: !prev[transactionId]
    }));
};


const filteredBrands = brands.filter(brand =>
    (brand.brandName || '').toLowerCase().includes(searchTerm.toLowerCase())
);

    // Fetch all brands and their wallet details
    const fetchBrands = async () => {
        setLoading(true);
        try {
            const usersRef = collection(db, 'users');
            const usersSnapshot = await getDocs(usersRef);

            const brandDetails = [];

            for (const userDoc of usersSnapshot.docs) {
                const userId = userDoc.id;
                const walletRef = doc(db, 'users', userId, 'wallet', 'walletDetails');
                const walletSnap = await getDoc(walletRef);

                if (walletSnap.exists()) {
                    const walletData = walletSnap.data();
                    brandDetails.push({
                        userId,
                        brandName: userDoc.data().brandName,
                        balance: walletData.balance,
                        lastUpdated: walletData.lastUpdated.toDate().toLocaleString(),
                    });
                }
            }

            setBrands(brandDetails);
        } catch (error) {
            console.error('Error fetching brands:', error);
            toast.error('Failed to fetch brands. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Fetch more details about the selected brand
    const fetchBrandDetails = async (userId) => {
        try {
            const userRef = doc(db, 'users', userId);
            const userSnap = await getDoc(userRef);
            
            if (userSnap.exists()) {
                const userData = userSnap.data();
                setSelectedBrandDetails({
                    userId,
                    brandName: userData.brandName,
                    address: userData.address || '',
                    city: userData.city || '',
                    state: userData.state || '',
                    pincode: userData.pincode || '',
                    gst: userData.gst || 'NA',
                });
            }
        } catch (error) {
            console.error('Error fetching brand details:', error);
            toast.error('Failed to fetch brand details.');
        }
    };

    // Fetch transactions for a selected brand
    const fetchTransactions = async (userId) => {
        setLoading(true);
        try {
            const transactionsCollection = collection(db, 'users', userId, 'wallet', 'walletDetails', 'transactions');
            const transactionsSnapshot = await getDocs(query(transactionsCollection, orderBy('timestamp', 'desc')));

            const allTransactions = transactionsSnapshot.docs.map((doc) => ({
                transactionId: doc.id,
                ...doc.data(),
            }));

            setTransactions(allTransactions);
        } catch (error) {
            console.error('Error fetching transactions:', error);
            toast.error('Failed to fetch transactions. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Handle the click of "Details" to show the brand's transaction history
    const handleDetailsClick = (userId) => {
        setSelectedBrand(userId);
        fetchTransactions(userId);
        fetchBrandDetails(userId);
    };

  
  // Format date for datetime-local input
const formatDateForInput = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
};

    const toWords = new ToWords({
        localeCode: "en-IN",
        converterOptions: {
          currency: true,
          ignoreDecimal: false,
          ignoreZeroCurrency: false,
          doNotAddOnly: false,
          currencyOptions: {
            name: "Rupee",
            plural: "Rupees",
            symbol: "₹",
            fractionalUnit: {
              name: "Paisa",
              plural: "Paise",
              symbol: "",
            },
          },
        },
    });

    // Generate a sequential credit note number
    const generateCreditNoteNumber = async () => {
        try {
            const currentDate = new Date();
            const month = currentDate.getMonth() + 1;
            
            // Determine fiscal year
            let fiscalYear;
            if (month >= 4) {
                // April onwards is the new fiscal year
                fiscalYear = `${currentDate.getFullYear()}-${currentDate.getFullYear() + 1}`;
            } else {
                // January to March is previous fiscal year
                fiscalYear = `${currentDate.getFullYear() - 1}-${currentDate.getFullYear()}`;
            }
            
            const creditNotePrefix = `CN/${fiscalYear}/`;
            
            // Get list of existing credit notes in Firebase Storage
            const storageRef = ref(Storage, `users/${selectedBrand}/creditNotes/`);
            const listResult = await listAll(storageRef);
            
            let nextCreditNoteNumber = 1; // Default to 1 if no credit notes exist
            
            if (listResult.items.length > 0) {
                // Extract the numeric part of each file name (e.g., 'CreditNote_CN_2025-2026_01.pdf')
                const creditNoteNumbers = listResult.items
                    .map(item => {
                        // Extract just the number part from the filename
                        const match = item.name.match(/CreditNote_CN_.*?_(\d+)\.pdf$/);
                        return match ? parseInt(match[1], 10) : 0;
                    })
                    .filter(num => num > 0); // Filter out invalid names
                
                // Find the highest credit note number and increment it
                if (creditNoteNumbers.length > 0) {
                    nextCreditNoteNumber = Math.max(...creditNoteNumbers) + 1;
                }
            }
            
            // Format the number with leading zeros (e.g., 01, 02, etc.)
            const formattedNumber = String(nextCreditNoteNumber).padStart(2, '0');
            return `${creditNotePrefix}${formattedNumber}`;
        } catch (error) {
            console.error('Error generating credit note number:', error);
            // Fallback to a timestamp-based number if there's an error
            return `CN/${new Date().getTime()}`;
        }
    };

    // Handle Add Transaction button click
    const handleAddTransaction = () => {
        setIsAddingTransaction(true);
        setIsEditingTransaction(false);
        setIsCreatingCreditNote(false);
        setTransactionToEdit(null);
        
        // Clear form inputs
        if (typeInputRef.current) typeInputRef.current.value = '';
        if (amountInputRef.current) amountInputRef.current.value = '';
        if (balanceBeforeInputRef.current) balanceBeforeInputRef.current.value = '';
        if (balanceAfterInputRef.current) balanceAfterInputRef.current.value = '';
        if (timestampInputRef.current) timestampInputRef.current.value = formatDateForInput(new Date());
    };

    // Handle Create Credit Note button click
    const handleCreateCreditNote = () => {
        setIsCreatingCreditNote(true);
        setIsAddingTransaction(false);
        setIsEditingTransaction(false);
        setTransactionToEdit(null);
        
        // Populate with brand details if available
        setTimeout(() => {
            if (selectedBrandDetails) {
                if (brandAddressRef.current) brandAddressRef.current.value = selectedBrandDetails.address || '';
                if (brandCityRef.current) brandCityRef.current.value = selectedBrandDetails.city || '';
                if (brandStateRef.current) brandStateRef.current.value = selectedBrandDetails.state || '';
                if (brandPincodeRef.current) brandPincodeRef.current.value = selectedBrandDetails.pincode || '';
                if (brandGstRef.current) brandGstRef.current.value = selectedBrandDetails.gst || 'NA';
            }
            
            // Clear other fields
            if (itemDescriptionRef.current) itemDescriptionRef.current.value = 'Fee Adjustment';
            if (creditNoteAmountRef.current) creditNoteAmountRef.current.value = '';
        }, 0);
    };

    // Handle Edit Transaction button click
    const handleEditClick = (transaction) => {
        setIsEditingTransaction(true);
        setIsAddingTransaction(false);
        setIsCreatingCreditNote(false);
        setTransactionToEdit(transaction.transactionId);
        
        // Convert Firestore timestamp to Date object for the input
        const timestampDate = transaction.timestamp instanceof Timestamp 
            ? transaction.timestamp.toDate() 
            : new Date(transaction.timestamp.seconds * 1000);
        
        // Format date to YYYY-MM-DDThh:mm
        const formattedDate = formatDateForInput(timestampDate);
        
        // Set values to the refs
        setTimeout(() => {
            if (typeInputRef.current) typeInputRef.current.value = transaction.type || '';
            if (amountInputRef.current) amountInputRef.current.value = transaction.amount?.toString() || '';
            if (balanceBeforeInputRef.current) balanceBeforeInputRef.current.value = transaction.balanceBefore?.toString() || '';
            if (balanceAfterInputRef.current) balanceAfterInputRef.current.value = transaction.balanceAfter?.toString() || '';
            if (timestampInputRef.current) timestampInputRef.current.value = formattedDate;
        }, 0);
    };


const handleAdjust = async (transaction) => {
    const currentIndex = transactions.findIndex(t => t.transactionId === transaction.transactionId);
    const prevTransaction = transactions[currentIndex + 1];
    const balanceBefore = prevTransaction ? prevTransaction.balanceAfter : 0;

    const isAddition = ['deposit', 'order cancellation', 'credit note'].some(keyword =>
        transaction.type?.toLowerCase().includes(keyword)
    );

    const balanceAfter = isAddition
        ? balanceBefore + transaction.amount
        : balanceBefore - transaction.amount;

    try {
        const transactionRef = doc(
            db, 'users', selectedBrand, 'wallet', 'walletDetails', 'transactions', transaction.transactionId
        );
        await updateDoc(transactionRef, { balanceBefore, balanceAfter });
        
        // Update local state directly, no refetch
        setTransactions(prev => prev.map(t =>
            t.transactionId === transaction.transactionId
                ? { ...t, balanceBefore, balanceAfter }
                : t
        ));

        toast.success(`Adjusted: Before ₹${balanceBefore.toFixed(2)} → After ₹${balanceAfter.toFixed(2)}`);
    } catch (error) {
        console.error('Error adjusting transaction:', error);
        toast.error('Failed to adjust transaction.');
    }
};

    // Generate Credit Note PDF
    const generateCreditNotePDF = (creditNoteData) => {
        return new Promise((resolve) => {
            const doc = new jsPDF();
            
            // Add company header
            doc.setFontSize(18);
            doc.setFont('helvetica', 'bold');
            doc.text('CREDIT NOTE', 105, 20, { align: 'center' });
            
            doc.setFontSize(12);
            doc.text('UNITEE STUDIOS PRIVATE LIMITED', 105, 30, { align: 'center' });
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(10);
            doc.text('Maharashtra, India', 105, 35, { align: 'center' });
            doc.text('GSTIN: 27AADCU3575G1Z5', 105, 40, { align: 'center' });
            doc.text('Email: rajprit@uniteeculture.com', 105, 45, { align: 'center' });
            doc.text('1ST FLOOR, PLOT 304/E/, 3, WAKHARIA BUILDING', 105, 50, { align: 'center' });
            doc.text('Vithalbhai Patel Road, MUMBAI', 105, 55, { align: 'center' });
            
            doc.text('Credit Note Date: ' + creditNoteData.date, 20, 70);
            doc.text('Credit Note Number: ' + creditNoteData.creditNoteNumber, 20, 75);
            
            // Bill To section
            doc.setFont('helvetica', 'bold');
            doc.text('Bill To', 20, 85);
            doc.setFont('helvetica', 'normal');
            doc.text('Name: ' + creditNoteData.brandName, 20, 90);
            doc.text('Address: ' + creditNoteData.address, 20, 95);
            doc.text('City: ' + creditNoteData.city, 20, 100);
            doc.text('State: ' + creditNoteData.state, 20, 105);
            doc.text('Pin Code: ' + creditNoteData.pincode, 20, 110);
            doc.text('GST Number: ' + creditNoteData.gst, 20, 115);
            
            // Credit Note Details
            doc.setFont('helvetica', 'bold');
            doc.text('Credit Note Details', 20, 130);
            
            // Create table for item details
            autoTable(doc,{
                startY: 135,
                head: [['Item & Description', 'HSN/SAC', 'Amount']],
                body: [
                    [creditNoteData.itemDescription, '998599', parseFloat(creditNoteData.amount).toFixed(2)]
                ],
                theme: 'grid',
                headStyles: { fillColor: [220, 220, 220], textColor: [0, 0, 0] },
                styles: { fontSize: 10 }
            });
            
            // Total amount
            const finalY = doc.lastAutoTable.finalY + 10;
            doc.setFont('helvetica', 'bold');
            doc.text('Total: '  + parseFloat(creditNoteData.amount).toFixed(2), 150, finalY);
            
            // Amount in words
            doc.text('Amount (in words): ' + toWords.convert(parseFloat(creditNoteData.amount)),  20, finalY + 10);
            
            // Authorized Signature
            doc.text('Authorized Signature', 150, finalY + 30);
            doc.text('Thanks for your business.', 20, finalY + 45);
            
            // Convert to blob
            const pdfBlob = doc.output('blob');
            resolve(pdfBlob);
        });
    };

    // Upload PDF to Firebase Storage
    const uploadCreditNotePDF = async (pdfBlob, creditNoteNumber) => {
        return new Promise((resolve, reject) => {
            try {
                // Format the filename with underscores instead of slashes for storage
                const formattedCreditNoteNumber = creditNoteNumber.replace(/\//g, '_');
                const fileName = `CreditNote_${formattedCreditNoteNumber}.pdf`;
                const folderPath = `creditNotes/`;
                const creditNoteRef = ref(Storage, `${folderPath}${fileName}`);
                
                const uploadTask = uploadBytesResumable(creditNoteRef, pdfBlob);
                
                uploadTask.on(
                    'state_changed',
                    snapshot => {
                        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                        console.log(`Credit Note Upload is ${progress}% done`);
                    },
                    error => {
                        console.error('Error uploading credit note:', error);
                        reject(error);
                    },
                    async () => {
                        try {
                            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                            console.log('Credit Note uploaded, download URL:', downloadURL);
                            resolve(downloadURL);
                        } catch (err) {
                            console.error('Error handling completed credit note upload:', err);
                            reject(err);
                        }
                    }
                );
            } catch (error) {
                console.error('Error in upload function:', error);
                reject(error);
            }
        });
    };

    // Save Credit Note and create transaction
    const saveCreditNote = async () => {
        // Get values from refs
        const address = brandAddressRef.current?.value || '';
        const city = brandCityRef.current?.value || '';
        const state = brandStateRef.current?.value || '';
        const pincode = brandPincodeRef.current?.value || '';
        const gst = brandGstRef.current?.value || 'NA';
        const itemDescription = itemDescriptionRef.current?.value || 'Fee Adjustment';
        const amountStr = creditNoteAmountRef.current?.value || '';
        
        if (!address || !city || !state || !pincode || !amountStr) {
            toast.error('Please fill in all required fields');
            return;
        }

        // Convert to proper types
        const amount = parseFloat(amountStr);
        
        // Validate numeric values
        if (isNaN(amount)) {
            toast.error('Please enter a valid amount');
            return;
        }

        try {
            setLoading(true);
            
            // Get current wallet balance
            const walletRef = doc(db, 'users', selectedBrand, 'wallet', 'walletDetails');
            const walletSnap = await getDoc(walletRef);
            
            if (!walletSnap.exists()) {
                toast.error('Wallet details not found');
                setLoading(false);
                return;
            }
            
            const walletData = walletSnap.data();
            const balanceBefore = walletData.balance;
            const balanceAfter = balanceBefore + amount;
            
            // Generate credit note number and date
            const creditNoteNumber = await generateCreditNoteNumber();
            const currentDate = new Date();
            const formattedDate = `${currentDate.getDate()} ${['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'][currentDate.getMonth()]} ${currentDate.getFullYear().toString().slice(-2)}`;
            
            // Prepare credit note data
            const brandName = selectedBrandDetails?.brandName || '';
            const creditNoteData = {
                creditNoteNumber,
                date: formattedDate,
                brandName,
                address,
                city,
                state,
                pincode,
                gst,
                itemDescription,
                amount,
            };
            
            // Generate PDF
            const pdfBlob = await generateCreditNotePDF(creditNoteData);
            
            // Upload PDF to Firebase Storage
            const creditNoteURL = await uploadCreditNotePDF(pdfBlob, creditNoteNumber);
            
            // Create transaction with credit note details
            const transactionData = {
                type: 'Credit Note',
                amount,
                balanceBefore,
                balanceAfter,
                timestamp: Timestamp.now(),
                creditNoteNumber,
                creditNoteURL,
                creditNoteDate: formattedDate,
                CNtype: "nonGSTcreditNote",

            };
            
            // Add transaction to Firestore
            const transactionsCollection = collection(
                db, 
                'users', 
                selectedBrand, 
                'wallet', 
                'walletDetails', 
                'transactions'
            );
            
            await addDoc(transactionsCollection, transactionData);
            
            // Update wallet balance
            await updateDoc(walletRef, {
                balance: balanceAfter,
                lastUpdated: Timestamp.now()
            });
            
            toast.success('Credit note created and transaction added successfully');
            
            // Refresh data
            fetchTransactions(selectedBrand);
            fetchBrands();
            
            // Reset form
            setIsCreatingCreditNote(false);
            
        } catch (error) {
            console.error('Error saving credit note:', error);
            toast.error('Failed to create credit note. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Save new transaction to Firestore
    const saveTransaction = async () => {
        // Get values from refs
        const type = typeInputRef.current?.value || '';
        const amountStr = amountInputRef.current?.value || '';
        const balanceBeforeStr = balanceBeforeInputRef.current?.value || '';
        const balanceAfterStr = balanceAfterInputRef.current?.value || '';
        const timestampStr = timestampInputRef.current?.value || '';
        
        if (!type || !amountStr || !balanceBeforeStr || !balanceAfterStr || !timestampStr) {
            toast.error('Please fill in all fields');
            return;
        }

        // Convert to proper types
        const amount = parseFloat(amountStr);
        const balanceBefore = parseFloat(balanceBeforeStr);
        const balanceAfter = parseFloat(balanceAfterStr);
        const timestamp = new Date(timestampStr);
        
        // Validate numeric values
        if (isNaN(amount) || isNaN(balanceBefore) || isNaN(balanceAfter) || isNaN(timestamp.getTime())) {
            toast.error('Please enter valid values for all fields');
            return;
        }

        try {
            setLoading(true);
            
            const transactionData = {
                type,
                amount,
                balanceBefore,
                balanceAfter,
                timestamp: Timestamp.fromDate(timestamp),
            };

            if (isEditingTransaction) {
                // Update existing transaction
                const transactionRef = doc(
                    db, 
                    'users', 
                    selectedBrand, 
                    'wallet', 
                    'walletDetails', 
                    'transactions', 
                    transactionToEdit
                );
                
                await updateDoc(transactionRef, transactionData);
                toast.success('Transaction updated successfully');
            } else {
                // Add new transaction
                const transactionsCollection = collection(
                    db, 
                    'users', 
                    selectedBrand, 
                    'wallet', 
                    'walletDetails', 
                    'transactions'
                );
                
                await addDoc(transactionsCollection, transactionData);
                toast.success('Transaction added successfully');
            }

            // Refresh transactions
            fetchTransactions(selectedBrand);
            
            // Update the wallet balance for the brand
            const walletRef = doc(db, 'users', selectedBrand, 'wallet', 'walletDetails');
            await updateDoc(walletRef, {
                balance: balanceAfter,
                lastUpdated: Timestamp.now()
            });
            
            // Refresh brands to show updated balance
            fetchBrands();
            
            // Reset form
            setIsAddingTransaction(false);
            setIsEditingTransaction(false);
            setTransactionToEdit(null);
            
        } catch (error) {
            console.error('Error saving transaction:', error);
            toast.error('Failed to save transaction. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Cancel add/edit mode
    const cancelAddEdit = () => {
        setIsAddingTransaction(false);
        setIsEditingTransaction(false);
        setIsCreatingCreditNote(false);
        setTransactionToEdit(null);
    };

    // View credit note
    const viewCreditNote = (url) => {
        if (url) {
            window.open(url, '_blank');
        } else {
            toast.error('Credit note URL not available');
        }
    };

    useEffect(() => {
        fetchBrands();
    }, []);

    // Transaction form component
    const TransactionForm = () => (
        <div className="transaction-form">
            <h3>{isEditingTransaction ? 'Edit Transaction' : 'Add New Transaction'}</h3>
            <div className="form-group">
                <label>Transaction Type:</label>
                <input
                    type="text"
                    ref={typeInputRef}
                    placeholder="e.g., Deposit, Withdrawal"
                    required
                />
            </div>
            <div className="form-group">
                <label>Amount (₹):</label>
                <input
                    type="text"
                    ref={amountInputRef}
                    placeholder="Amount"
                    required
                />
            </div>
            <div className="form-group">
                <label>Balance Before (₹):</label>
                <input
                    type="text"
                    ref={balanceBeforeInputRef}
                    placeholder="Balance Before"
                    required
                />
            </div>
            <div className="form-group">
                <label>Balance After (₹):</label>
                <input
                    type="text"
                    ref={balanceAfterInputRef}
                    placeholder="Balance After"
                    required
                />
            </div>
<div className="form-group">
    <label>Date and Time:</label>
    <input
        type="datetime-local"
        ref={timestampInputRef}
        step="1"
        required
    />
</div>
            <div className="form-buttons">
                <button onClick={saveTransaction}>
                    {isEditingTransaction ? 'Update' : 'Save'}
                </button>
                <button onClick={cancelAddEdit}>Cancel</button>
            </div>
        </div>
    );

    // Credit Note form component
    const CreditNoteForm = () => (
        <div className="credit-note-form">
            <h3>Create Credit Note</h3>
            
            <div className="form-header">
                <h4>Company Details</h4>
                <div className="company-details">
                    <p><strong>UNITEE STUDIOS PRIVATE LIMITED</strong></p>
                    <p>Maharashtra, India</p>
                    <p>GSTIN: 27AADCU3575G1Z5</p>
                    <p>Email: rajprit@uniteeculture.com</p>
                    <p>1ST FLOOR, PLOT 304/E/, 3, WAKHARIA BUILDING</p>
                    <p>Vithalbhai Patel Road, MUMBAI</p>
                </div>
            </div>
            
            <div className="form-section">
                <h4>Bill To</h4>
                <div className="form-group">
                    <label>Brand Name:</label>
                    <input
                        type="text"
                        value={selectedBrandDetails?.brandName || ''}
                        disabled
                    />
                </div>
                <div className="form-group">
                    <label>Address:</label>
                    <input
                        type="text"
                        ref={brandAddressRef}
                        placeholder="Address"
                        required
                    />
                </div>
                <div className="form-group">
                    <label>City:</label>
                    <input
                        type="text"
                        ref={brandCityRef}
                        placeholder="City"
                        required
                    />
                </div>
                <div className="form-group">
                    <label>State:</label>
                    <input
                        type="text"
                        ref={brandStateRef}
                        placeholder="State"
                        required
                    />
                </div>
                <div className="form-group">
                    <label>Pin Code:</label>
                    <input
                        type="text"
                        ref={brandPincodeRef}
                        placeholder="Pin Code"
                        required
                    />
                </div>
                <div className="form-group">
                    <label>GST Number:</label>
                    <input
                        type="text"
                        ref={brandGstRef}
                        placeholder="GST Number (if available)"
                    />
                </div>
            </div>
            
            <div className="form-section">
                <h4>Credit Note Details</h4>
                <div className="form-group">
                    <label>Item Description:</label>
                    <input
                        type="text"
                        ref={itemDescriptionRef}
                        defaultValue="Fee Adjustment"
                        required
                    />
                </div>
                <div className="form-group">
                    <label>HSN/SAC:</label>
                    <input
                        type="text"
                        value="998599"
                        disabled
                    />
                </div>
                <div className="form-group">
                    <label>Amount (₹):</label>
                    <input
                        type="text"
                        ref={creditNoteAmountRef}
                        placeholder="Amount"
                        required
                    />
                </div>
            </div>
            
            <div className="form-buttons">
                <button onClick={saveCreditNote}>Generate Credit Note</button>
                <button onClick={cancelAddEdit}>Cancel</button>
            </div>
        </div>
    );

    return (
        <div className="transactions-page">
            <ToastContainer position="top-center" autoClose={5000} hideProgressBar />
            {loading ? (
                <p>Loading...</p>
            ) : (
                <>
                  {!selectedBrand ? (
    <div>
        <h2>Brands Overview</h2>
        <div className="search-bar" style={{
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
        }}>
            <input
                type="text"
                placeholder="Search brand name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                    padding: '10px',
                    fontSize: '14px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    width: '300px'
                }}
            />
            {searchTerm && (
                <button 
                    onClick={() => setSearchTerm('')}
                    style={{
                        padding: '8px 12px',
                        fontSize: '14px',
                        cursor: 'pointer',
                        backgroundColor: '#f0f0f0',
                        border: '1px solid #ccc',
                        borderRadius: '4px'
                    }}
                >
                    Clear
                </button>
            )}
        </div>
        <table className="brands-table">
            <thead>
                <tr>
                    <th>Brand Name</th>
                    <th>Balance (₹)</th>
                    <th>Last Updated</th>
                    <th>Details</th>
                </tr>
            </thead>
            <tbody>
                {filteredBrands.length > 0 ? (
                    filteredBrands.map((brand) => (
                        <tr key={brand.userId}>
                            <td>{brand.brandName}</td>
                            <td>{brand.balance}</td>
                            <td>{brand.lastUpdated}</td>
                            <td>
                                <button onClick={() => handleDetailsClick(brand.userId)}>
                                    View Details
                                </button>
                            </td>
                        </tr>
                    ))
                ) : (
                    <tr>
                        <td colSpan="4">
                            {searchTerm ? 'No brands found matching your search' : 'No brands found'}
                        </td>
                    </tr>
                )}
            </tbody>
        </table>
    </div>
                    ) : (
                        <div>
                            <h2>Transaction Details for {brands.find(brand => brand.userId === selectedBrand)?.brandName}</h2>
                            <div className="transaction-controls">
                                <button onClick={() => setSelectedBrand(null)}>Back to Overview</button>
                                <button onClick={handleAddTransaction}>Add New Transaction</button>
                                <button onClick={handleCreateCreditNote}>Create Credit Note</button>
                            </div>
                            
                            {isAddingTransaction && <TransactionForm />}
                            {isCreatingCreditNote && <CreditNoteForm />}
                            {isEditingTransaction && <TransactionForm />}
                            
                            <table className="transactions-table">
                                <thead>
                                    <tr>
                                        <th>Transaction Type</th>
                                        <th>Amount (₹)</th>
                                        <th>Balance Before (₹)</th>
                                        <th>Balance After (₹)</th>
                                        <th>Date</th>
                                        <th>Order Id</th>
                                        <th>Credit Note</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {transactions.length > 0 ? (
                                        transactions.map((transaction) => (
                                            <tr key={transaction.transactionId}>
                                                <td>
    {transaction.type}
    {transaction.type?.toLowerCase().includes('bulk') && transaction.orderDetails && transaction.orderDetails.length > 0 && (
        <>
            <button 
                className="toggle-bulk-details"
                onClick={() => toggleBulkOrderDetails(transaction.transactionId)}
                style={{
                    marginLeft: '10px',
                    padding: '2px 8px',
                    fontSize: '12px',
                    cursor: 'pointer'
                }}
            >
                {expandedBulkOrders[transaction.transactionId] ? '▼' : '▶'}
            </button>
            {expandedBulkOrders[transaction.transactionId] && (
                <div className="bulk-order-details" style={{
                    marginTop: '10px',
                    padding: '10px',
                    backgroundColor: '#f5f5f5',
                    borderRadius: '4px',
                    maxHeight: '300px',
                    overflowY: 'auto'
                }}>
                    <table style={{width: '100%', fontSize: '12px'}}>
                        <thead>
                            <tr>
                                <th>Order ID</th>
                                <th>Store Order ID</th>
                                <th>Amount (₹)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {transaction.orderDetails.map((order, index) => (
                                <tr key={index}>
                                    <td>{order.orderId}</td>
                                    <td>{order.storeOrderId || 'N/A'}</td>
                                    <td>{order.amount?.toFixed(2) || 'N/A'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </>
    )}
</td>
                                                <td>{transaction.amount !== undefined && transaction.amount !== null ? transaction.amount.toFixed(2) : 'NA'}</td>
                                                <td>{transaction.balanceBefore !== undefined && transaction.balanceBefore !== null ? transaction.balanceBefore.toFixed(2) : 'NA'}</td>
                                                <td>{transaction.balanceAfter !== undefined && transaction.balanceAfter !== null ? transaction.balanceAfter.toFixed(2) : 'NA'}</td>
                                                <td>{new Date(transaction.timestamp.seconds * 1000).toLocaleString()}</td>
                                                <td>{transaction.orderId ||''}</td>
                                                <td>
                                                    {transaction.creditNoteURL ? (
                                                        <button 
                                                            className="view-credit-note-btn" 
                                                            onClick={() => viewCreditNote(transaction.creditNoteURL)}
                                                        >
                                                            View ({transaction.creditNoteNumber})
                                                        </button>
                                                    ) : (
                                                        'N/A'
                                                    )}
                                                </td>
                                                <td>
                                                    <button onClick={() => handleEditClick(transaction)}>Edit</button>
                                                        <button 
        onClick={() => handleAdjust(transaction)}
        style={{ marginLeft: '6px', backgroundColor: '#f0ad4e' }}
    >
        Adjust
    </button>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="7">No transactions found</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default Transactions;